import {
  canFillClinicalForms,
  canReviewClinicalForms,
  isSurveyor,
} from "../../patientPermissions";

/**
 * Mirrors backend `buildFormPermissions` for Eval & Orders clinical forms.
 *
 * Co-sign is role-based (Libraries): e.g. any OTA creator → any OT signer.
 */
export function buildClinicalFormPermissions(status, role, cosign = null) {
  const isReviewer = canReviewClinicalForms(role);
  const canWriteContent = canFillClinicalForms(role) && !isSurveyor(role);
  const current = status || "pending";

  let canEdit = false;
  let lockedReason = null;

  if (!canWriteContent) {
    canEdit = false;
    lockedReason = "This form is read-only for your role.";
  } else if (isReviewer) {
    canEdit = current !== "completed";
    if (!canEdit) {
      lockedReason =
        "This form is completed. Move it back to To Be Corrected to make changes.";
    }
  } else {
    canEdit =
      current === "pending" ||
      current === "to_be_corrected" ||
      current === "to_be_signed";
    if (!canEdit) {
      lockedReason =
        current === "send_to_office"
          ? "This form has been sent to the office. Only Admin/OA can change it now."
          : "This form is completed and locked. Only Admin/OA can reopen it.";
    }
  }

  let canSendToOffice = canWriteContent && !isReviewer;
  let canSignSignatures = canEdit && !isReviewer;
  let canToBeSign = false;
  let hideClinicalSignature = false;

  const mapping = cosign?.mapping || null;
  const filledByRole = cosign?.formFilledByRole ?? null;

  if (mapping && !isReviewer && canWriteContent) {
    const isMappedCreator =
      role === mapping.formCreatorRoleName &&
      (filledByRole == null || filledByRole === mapping.formCreatorRoleName);
    const isDesignatedSigner =
      role === mapping.formSignerRoleName &&
      filledByRole === mapping.formCreatorRoleName;

    if (isMappedCreator) {
      canSignSignatures = false;
      hideClinicalSignature = true;
      // Clinicians still Save / Sent to Office. Co-sign To Be Sign is extra.
      canSendToOffice = canEdit;
      canToBeSign =
        canEdit && (current === "pending" || current === "to_be_corrected");
      if (current === "to_be_signed") {
        canEdit = false;
        canToBeSign = false;
        lockedReason =
          "Waiting for a co-signer to review and sign this form.";
      }
    } else if (isDesignatedSigner) {
      if (current === "to_be_signed") {
        canEdit = true;
        canSignSignatures = true;
        canSendToOffice = true;
        canToBeSign = false;
        lockedReason = null;
      } else {
        canSendToOffice = false;
        canSignSignatures = false;
        canToBeSign = false;
      }
    }
  }

  return {
    canEdit,
    canSendToOffice,
    canToBeSign,
    hideClinicalSignature,
    canReview: canWriteContent && isReviewer,
    canViewPatientSignature: true,
    canSignSignatures,
    lockedReason,
  };
}
